/**
 * ChallengeSelect.jsx — pick a goal, set your parameters, race the physics.
 * Unlocked by attempting the Exam.
 */
import { useGame } from "./store";
import { initAudio } from "./sound";

const GOALS = [
  {
    id: "energy",
    icon: "⚡",
    name: "Least Energy",
    desc: "Finish the lap using the least engine work. Coast the downhills, accelerate gently, avoid braking away energy you paid for.",
    hint: "Physics hint: friction wastes μ·N per metre — low μ helps, but then curves allow less speed.",
  },
  {
    id: "time",
    icon: "⏱",
    name: "Least Time",
    desc: "Fastest lap wins — but every derailment costs you a respawn. Speed through straights, judge the curves perfectly.",
    hint: "Physics hint: higher μ raises curve limits (v_max = √(g·r·(cosθ+μ))) but drags you on straights.",
  },
  {
    id: "risk",
    icon: "⚠",
    name: "Least Risk",
    desc: "Spend the least time above 90% grip. A perfectly safe lap is possible — drive with margin everywhere.",
    hint: "Physics hint: risk counts every second your v²/r demand exceeds 90% of supply.",
  },
  {
    id: "wear",
    icon: "🛞",
    name: "Least Wear",
    desc: "Minimise friction damage (μ·N × distance). Watch for sparks — they ARE your wear, made visible.",
    hint: "Physics hint: wear ∝ μ — but with low μ, can you still hold the curves?",
  },
];

export default function ChallengeSelect() {
  const closeChallengeSelect = useGame((s) => s.closeChallengeSelect);
  const startChallenge = useGame((s) => s.startChallenge);
  const startGame = useGame((s) => s.startGame);
  const challengeMu = useGame((s) => s.challengeMu);
  const setChallengeMu = useGame((s) => s.setChallengeMu);

  const begin = (goal) => {
    initAudio();
    startChallenge(goal);
    startGame();
  };

  return (
    <div
      data-testid="challenge-select"
      style={{
        position: "fixed", inset: 0, zIndex: 46,
        background: "rgba(2,4,12,0.97)", overflowY: "auto",
        color: "#fff", fontFamily: "'Inter', system-ui, sans-serif",
        padding: "30px 16px 60px",
      }}
    >
      <div style={{ width: "min(860px, 100%)", margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 4, color: "#ffd23f", fontWeight: 700 }}>🏁 CHALLENGE MODE</div>
            <h1 style={{ margin: "4px 0", fontSize: 30, fontWeight: 900 }}>Optimize the Lap</h1>
          </div>
          <button
            onClick={closeChallengeSelect}
            style={{ padding: "10px 18px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer" }}
          >
            ← Back
          </button>
        </div>
        <p style={{ color: "#9aa4b2", fontSize: 14, marginBottom: 20 }}>
          No question pauses here — pure driving, with one new power: the <b style={{ color: "#ff8a3d" }}>friction slider</b>.
          Pick a goal; at the end you'll see your numbers against the physics optimum.
        </p>

        {/* Friction slider */}
        <div style={{ background: "rgba(10,12,24,0.85)", border: "1px solid rgba(255,138,61,0.4)", borderRadius: 14, padding: "16px 20px", marginBottom: 22 }}>
          <label style={{ fontSize: 12, color: "#ff8a3d", fontWeight: 800, letterSpacing: 1 }}>
            TRACK FRICTION μ = {challengeMu.toFixed(2)}
          </label>
          <input
            type="range" min={0.05} max={0.8} step={0.05} value={challengeMu}
            onChange={(e) => setChallengeMu(parseFloat(e.target.value))}
            style={{ width: "100%", marginTop: 8 }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "#9aa4b2", marginTop: 4 }}>
            <span>low μ: efficient & gentle, but curves allow less speed</span>
            <span>high μ: grippy & fast in curves, but wasteful — expect sparks ✨</span>
          </div>
          <div style={{ fontSize: 12.5, color: "#ffd23f", marginTop: 8, fontFamily: "'JetBrains Mono', monospace" }}>
            Curve 1 limit with this μ: v_max ≈ {Math.sqrt(10 * 38.6 * (1 + challengeMu)).toFixed(1)} m/s
            &nbsp;(was 19.6 m/s at μ ≈ 0)
          </div>
        </div>

        {/* Goal cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          {GOALS.map((goal) => (
            <div
              key={goal.id}
              style={{ background: "rgba(10,12,24,0.85)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 14, padding: "18px 18px 16px", display: "flex", flexDirection: "column" }}
            >
              <div style={{ fontSize: 26 }}>{goal.icon}</div>
              <div style={{ fontSize: 17, fontWeight: 800, margin: "6px 0" }}>{goal.name}</div>
              <div style={{ fontSize: 13, color: "#cfd6e3", lineHeight: 1.55, flex: 1 }}>{goal.desc}</div>
              <div style={{ fontSize: 11.5, color: "#9fe8c9", lineHeight: 1.5, margin: "10px 0" }}>{goal.hint}</div>
              <button
                data-testid={`challenge-${goal.id}`}
                onClick={() => begin(goal.id)}
                style={{ padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #ffd23f, #ff8a3d)", color: "#1a0d00", fontWeight: 800, fontSize: 14, cursor: "pointer" }}
              >
                ▶ START
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
